import { Vehicle } from '@/types';

/**
 * Generates a Product schema for a vehicle (Schema.org)
 */
export const generateVehicleProductSchema = (vehicle: Vehicle, baseUrl: string = 'https://trucks52-sarl.lovable.app') => {
  return {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": vehicle.title,
    "description": vehicle.description || `${vehicle.title} - Véhicule industriel d'occasion chez Trucks 52 SARL`,
    "image": vehicle.images?.[0] || '',
    "brand": {
      "@type": "Brand",
      "name": vehicle.brand || "Non spécifié"
    },
    "offers": {
      "@type": "Offer",
      "url": `${baseUrl}/?vehicle=${vehicle.id}`,
      "priceCurrency": "EUR",
      "price": vehicle.priceTTC || vehicle.price || 0,
      "availability": vehicle.isAvailable ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "Trucks 52 SARL"
      }
    },
    "category": vehicle.category,
    "vehicleConfiguration": vehicle.axleConfiguration || undefined,
    "mileageFromOdometer": vehicle.mileage ? {
      "@type": "QuantitativeValue",
      "value": vehicle.mileage,
      "unitCode": "KMT"
    } : undefined,
    "productionDate": vehicle.year?.toString(),
    "fuelType": vehicle.fuelType,
    "vehicleTransmission": vehicle.transmission,
    "vehicleEngine": vehicle.enginePower ? {
      "@type": "EngineSpecification",
      "enginePower": {
        "@type": "QuantitativeValue",
        "value": vehicle.enginePower,
        "unitCode": "BHP"
      },
      "engineDisplacement": vehicle.engineDisplacement ? {
        "@type": "QuantitativeValue",
        "value": vehicle.engineDisplacement,
        "unitCode": "LTR"
      } : undefined
    } : undefined,
    "additionalProperty": [
      vehicle.euroStandard ? {
        "@type": "PropertyValue",
        "name": "Norme Euro",
        "value": `Euro ${vehicle.euroStandard}`
      } : null,
      vehicle.critAir ? {
        "@type": "PropertyValue",
        "name": "Vignette Crit'Air",
        "value": vehicle.critAir === 'exempt' ? 'Exempté' : `Crit'Air ${vehicle.critAir}`
      } : null,
      vehicle.maxWeight ? {
        "@type": "PropertyValue",
        "name": "PTAC",
        "value": `${vehicle.maxWeight} kg`
      } : null,
      vehicle.payloadCapacity ? {
        "@type": "PropertyValue",
        "name": "Charge utile",
        "value": `${vehicle.payloadCapacity} kg`
      } : null
    ].filter(Boolean)
  };
};

/**
 * Generates Organization schema for Trucks 52 SARL
 */
export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://trucks52-sarl.lovable.app/#organization",
    "name": "Trucks 52 SARL",
    "description": "Expert en vente de véhicules industriels d'occasion en Haute-Marne : camions, tracteurs routiers, semi-remorques, utilitaires. Garantie, financement, reprise.",
    "url": "https://trucks52-sarl.lovable.app",
    "logo": "https://trucks52-sarl.lovable.app/lovable-uploads/6d0b544e-d8d8-4f71-9ceb-c4652b9364aa.png",
    "image": "https://trucks52-sarl.lovable.app/lovable-uploads/6d0b544e-d8d8-4f71-9ceb-c4652b9364aa.png",
    "telephone": "+33608248529",
    "email": "trucks.52@orange.fr",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2 Place de l'Église, Recourt",
      "addressLocality": "Val-de-Meuse",
      "postalCode": "52140",
      "addressRegion": "Haute-Marne",
      "addressCountry": "FR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "48.0942",
      "longitude": "5.4964"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "09:00",
        "closes": "12:00"
      }
    ],
    "priceRange": "€€",
    "areaServed": [
      {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "48.0942",
          "longitude": "5.4964"
        },
        "geoRadius": "500000"
      }
    ],
    "founder": {
      "@type": "Person",
      "name": "Laurent DESNOUVEAUX",
      "jobTitle": "Directeur"
    },
    "sameAs": []
  };
};

/**
 * Generates BreadcrumbList schema
 */
export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};

/**
 * Generates FAQPage schema
 */
export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};